// Reusable Address validation config (engine-supported rules)
import type { ValidationConfig } from '../validationEngine/types';
import { getRequiredRulesOnly, getRules } from './validationConfigRuleUtils';

export const addressValidationConfig: ValidationConfig = {
    streetAddress: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: "${label || 'Street Address'} is required."
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 60 },
                messages: {
                    inline: "${label || 'Street address'} can’t be longer than 60 characters.",
                    gcalert: "${label || 'Street Address'} cannot be longer than 60 characters."
                }
            },
            { type: 'MAX_LENGTH', params: { max: 60 }, messages: "${label || 'Street Address'} must be under 60 characters" },
            {
                type: 'REGEX',
                params: { regex: /^[A-Za-z0-9 #\\\-/.',]*$/ },
                messages: "Use only letters, numbers, and # / \\ - . ' ,"
            }
        ]
    },
    streetAddress2: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 60 },
                messages: {
                    inline: 'Street Address 2 can’t be longer than 60 characters.',
                    gcalert: 'Street Address 2 cannot be longer than 60 characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 60 }, messages: 'Street Address 2 must be under 60 characters' },
            {
                type: 'REGEX',
                params: { regex: /^[A-Za-z0-9 #\\\-/.',:]*$/ },
                messages: "Use only letters, numbers, and # / \\ - . ' , :"
            }
        ]
    },
    city: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'City is required.'
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 30 },
                messages: {
                    inline: 'City can’t be longer than 30 characters.',
                    gcalert: 'City cannot be longer than 30 characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 30 }, messages: 'City must be under 30 characters' },
            { type: 'REGEX', params: { regex: /^[A-Za-z0-9 #\-/.']*$/ }, messages: "Use only letters, numbers, and # / - . '" }
        ]
    },
    state: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 2 },
                messages: {
                    inline: 'State can’t be longer than 2 characters.',
                    gcalert: 'State cannot be longer than 2 characters.'
                }
            }
        ]
    },
    zip: {
        rules: [
            {
                type: 'REQUIRED',
                messages: 'Postal Code is required',
                conditions: { applicableEntities: ['customer'], excludeDealModes: ['OptionalDesking'] }
            },
            {
                type: 'MIN_LENGTH',
                flow: 'contract',
                params: { min: 5 },
                messages: {
                    inline: 'Postal code must be at least 5 characters.',
                    gcalert: 'Postal Code cannot be less than 5 characters.'
                }
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 9 },
                messages: {
                    inline: 'Postal code can’t be longer than 9 characters.',
                    gcalert: 'Postal Code cannot be longer than 9 characters.'
                }
            },
            {
                type: 'CUSTOM',
                params: {
                    evaluator: (value: any) => {
                        if (!value) return true;
                        const s = String(value);
                        const zipRegex = /^\d{5}(?:[-]\d{4})?$|^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
                        return s.length <= 10 && zipRegex.test(s);
                    }
                },
                messages: 'Format must be 12345'
            }
        ]
    },
    county: {
        rules: [
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: 20 },
                messages: {
                    inline: 'County can’t be longer than 20 characters.',
                    gcalert: 'County cannot be longer than 20 characters.'
                }
            },
            {
                type: 'REGEX',
                flow: 'contract',
                params: { regex: /^[A-Za-z0-9\s]+$/ },
                messages: {
                    inline: 'County can’t include special characters.',
                    gcalert: 'County cannot have special characters.'
                }
            },
            { type: 'MAX_LENGTH', params: { max: 30 }, messages: 'County must be under 30 characters' },
            { type: 'REGEX', params: { regex: /^[A-Za-z0-9\-'.\s]+$/ }, messages: "Use only letters, numbers, and - . ' ," }
        ]
    }
};

export const POSTAL_CODE_DIGITS_ONLY_TOGGLE = 'US2125037_MV_Postal_Code_Digits_Only_Validation';

const customerZipFormatRule = {
    type: 'CUSTOM' as const,
    flow: 'default+contract',
    conditions: { enabledToggles: POSTAL_CODE_DIGITS_ONLY_TOGGLE },
    params: {
        evaluator: (value: any) => !value || /^\d{5}(?:\d{4})?$/.test(String(value))
    },
    messages: 'Enter 12345 or 12345-1234'
};

export const customerAddressValidationConfig: ValidationConfig = {
    ...addressValidationConfig,
    zip: {
        rules: [
            ...getRequiredRulesOnly(getRules(addressValidationConfig.zip)),
            ...getRules(addressValidationConfig.zip)
                .filter((rule) => rule.type !== 'REQUIRED')
                .map((rule) => ({
                    ...rule,
                    conditions: { ...rule.conditions, disabledToggles: POSTAL_CODE_DIGITS_ONLY_TOGGLE }
                })),
            customerZipFormatRule
        ]
    }
};
